import { AnalysisItem, AnalysisType, GroupKey, GroupType, projectAnalysis, ProjectAnalysisEvents } from "@/types/analysis/ProjectAnalysis";
import type { ProjectAnalysisInput } from "@/types/analysis/ProjectAnalysisInput";
import { ColorDefinition } from "@/types/colors";
import { AppSettingType } from "@api/apiClient";
import { useCadModelCategories } from "@api/cadModelCategories";
import DataGrid from "@components/DataGrid/DataGrid";
import MicroChip from "@components/MicroChip/MicroChip";
import { getSubSystemNameById, getSystemNameById } from "@features/analysis/utils/metadataEntityUtils";
import useAppStore from "@hooks/useAppStore";
import useSelectionManager from "@hooks/useSelectionManager";
import i18n from "@i18n/i18n";
import SquareIcon from "@mui/icons-material/Square";
import { Box, Tooltip, Typography } from "@mui/material";
import { GridCellParams, GridColDef, GridColumnHeaderParams, GridInputRowSelectionModel } from "@mui/x-data-grid-premium";
import sortArrays from "@utils/sortArrays";
import { Emitter, EventType } from "mitt";
import { observer } from "mobx-react";
import { useEffect, useState } from "react";
import { ResultsEvent, ResultsEvents } from "../../ResultsEvents";
import { isolateViewersByAttribute } from "./viewerUtils";

export interface ByAttributesDataGridProps {
  analysisInput: ProjectAnalysisInput | null;
  emitter: Emitter<Record<EventType, unknown>>;
  attributeType: AnalysisType;
}

type MyGridColDef = GridColDef & {
  id?: number;
  groupKey?: GroupKey;
  name?: string;
  scenarioName?: string;
  modelName?: string;
  color?: string;
  [key: string]: any; // Allow dynamic column properties
};

type MyGridRow = {
  id: number;
  groupKey: string;
  name: string;
  color: string;
  [key: string]: any; // Allow dynamic column properties
};

const ByAttributesDataGrid = observer((props: ByAttributesDataGridProps) => {
  console.log("ByAttributesDataGrid: ", props);
  const appStore = useAppStore();
  const selectionManager = useSelectionManager();
  const cadModelCategories = useCadModelCategories();
  const [rows, setRows] = useState<MyGridRow[]>([]);
  const [columns, setColumns] = useState<MyGridColDef[]>([]);
  const [aggregationModel, setAggregationModel] = useState({});
  const [rowSelectionModel, setRowSelectionModel] = useState<GridInputRowSelectionModel>([]);
  const [rowHighlights, setRowHighlights] = useState<Set<GroupKey>>(new Set());

  useEffect(() => {
    updateGridData();
    updateGridHighlights();
  }, []);

  useEffect(() => {
    updateGridData();
    updateGridHighlights();
  }, [props.attributeType]);

  useEffect(() => {
    projectAnalysis.emitter.on(ProjectAnalysisEvents.ANALYSIS_CHANGED_EVENT, updateGridData);

    return () => {
      projectAnalysis.emitter.off(ProjectAnalysisEvents.ANALYSIS_CHANGED_EVENT, updateGridData);
    };
  }, []);

  useEffect(() => {
    projectAnalysis.emitter.on(ProjectAnalysisEvents.SELECTION_CHANGED_EVENT, updateGridHighlights);

    return () => {
      projectAnalysis.emitter.off(ProjectAnalysisEvents.SELECTION_CHANGED_EVENT, updateGridHighlights);
    };
  }, []);

  useEffect(() => {
    props.emitter.on(ResultsEvents.SELECTION_CHANGED, handleUpdateSelection);

    return () => {
      props.emitter.off(ResultsEvents.SELECTION_CHANGED, handleUpdateSelection);
    };
  }, [rows]);

  const handleUpdateSelection = async (event: any) => {
    if (event.key === "geometry-loaded") {
      updateViewers(event.groupKey);
    } else if (event.key !== "data-grid") {
      updateSelection(event.groupKey);
    }
  };

  const updateGridHighlights = async () => {
    const highlights = projectAnalysis.getSelectedElementsByAnalysisType(selectionManager, props.analysisInput, props.attributeType) || new Set();
    setRowHighlights(highlights);
  };

  const updateSelection = (groupKey?: number | string) => {
    if (groupKey) {
      const row = rows.find((r: MyGridRow) => r.name === groupKey);
      if (row) {
        setRowSelectionModel([row.id]);
        return;
      }
    }

    setRowSelectionModel([]);
  };

  const updateGridData = async () => {
    // Get the analysis data from ProjectAnalysis based on the current analysis type
    const analysisData = projectAnalysis.getAnalysis(props.attributeType);

    console.log("ByAttributesDataGrid updateGridData:", {
      attributeType: props.attributeType,
      hasAnalysisData: !!analysisData,
      hasAnalysisInput: !!props.analysisInput,
      analysisDataKeys: analysisData ? Array.from(analysisData.scenarioAnalyses.keys()) : [],
    });

    if (!analysisData || !props.analysisInput) {
      setRows([]);
      setColumns(getColDefs());
      setAggregationModel({});
      return;
    }

    const aggregationModel: any = {};
    const newColDefs: MyGridColDef[] = [...colDefs];
    const keys = analysisData.getGroupKeys();
    let newRows = keys.map((key, index) => ({ id: index, groupKey: key, name: "" })) as MyGridRow[];
    let s = 0;
    let i = 1;

    analysisData.scenarioAnalyses.forEach((scenarioAnalysis) => {
      scenarioAnalysis.groupData.forEach((groupData) => {
        s = scenarioAnalysis.scenarioIndex + 1;

        // Add quantity column
        newColDefs.push({
          field: `col${i}`,
          headerName: `${i18n.t("results-tab.title.quantity")}`,
          flex: 1,
          type: "number",
          minWidth: 100,
        });
        aggregationModel[`col${i}`] = "sum";

        Array.from(groupData.items.values()).forEach((item: AnalysisItem, index) => {
          if (newRows[index]) {
            newRows[index] = {
              ...newRows[index],
              [`col${i}`]: item.elementCount,
            };
          }
        });
        i++;

        // Add CO2e column
        newColDefs.push({
          field: `col${i}`,
          scenarioName: scenarioAnalysis.name,
          modelName: groupData.name,
          headerName: projectAnalysis.groupType === GroupType.Model ? `S${s}-M${groupData.groupIndex + 1}` : `S${s}`,
          renderHeader,
          valueFormatter: renderFormattedNumber,
          flex: 1,
          type: "number",
          minWidth: 150,
        });
        aggregationModel[`col${i}`] = "sum";

        Array.from(groupData.items.values()).forEach((item: AnalysisItem, index) => {
          if (newRows[index]) {
            newRows[index] = {
              ...newRows[index],
              name: resolveDisplayName(String(item.groupKey), props.attributeType),
              [`col${i}`]: item.co2eTotal,
            };
          }
        });
        i++;
      });
      s++;
    });

    // Sort newRows by id in descending order
    const gridColors = analysisData.colorDefinitions();
    const sorted = sortArrays({ newRows, gridColors }, (a, b) => b.id! - a.id!) as { newRows: MyGridRow[]; gridColors: ColorDefinition[] };
    newRows = sorted.newRows.map((value: MyGridRow, index: number) => ({ ...value, color: sorted.gridColors[index].hex }));

    setColumns([...newColDefs]);
    setRows(newRows);
    setAggregationModel(aggregationModel);
  };

  const resolveDisplayName = (value: string, attributeType: AnalysisType): string => {
    if (attributeType === AnalysisType.System) {
      return getSystemNameById(appStore.metadataEntities, value) || value;
    }
    if (attributeType === AnalysisType.SubSystem) {
      return getSubSystemNameById(appStore.metadataEntities, value) || value;
    }
    return value;
  };

  const renderHeader = (params: GridColumnHeaderParams) => {
    const { colDef }: { colDef: MyGridColDef } = params;
    //console.log("PARAMS", params);
    return (
      <Tooltip
        title={
          <>
            <Typography variant="caption">{colDef.scenarioName}</Typography>
            {projectAnalysis.groupType === GroupType.Model && <Typography variant="subtitle1">{colDef.modelName}</Typography>}
          </>
        }
      >
        <Box>
          <MicroChip label={`${params.colDef.headerName}`} />
          <Box ml={1} component={"span"}>
            {i18n.t("results-tab.title.co2e")}
          </Box>
        </Box>
      </Tooltip>
    );
  };

  const renderFormattedNumber = (value?: number) => {
    if (value === undefined || value === null) {
      return "";
    }

    return i18n.t("number", { value, digits: AppSettingType.NumberFormat_EC_Digits });
  };

  const getColDefs = (): MyGridColDef[] => {
    const getHeaderName = (attributeType: AnalysisType): string => {
      switch (attributeType) {
        case AnalysisType.System:
          return i18n.t("results-tab.title.system");
        case AnalysisType.SubSystem:
          return i18n.t("results-tab.title.sub-system");
        case AnalysisType.Attribute1:
          return i18n.t("results-tab.title.attribute1");
        case AnalysisType.Attribute2:
          return i18n.t("results-tab.title.attribute2");
        case AnalysisType.Attribute3:
          return i18n.t("results-tab.title.attribute3");
        default:
          return i18n.t("results-tab.title.system");
      }
    };

    return [
      {
        field: "name",
        headerName: getHeaderName(props.attributeType),
        flex: 1,
        minWidth: 200,
        renderCell: (params: GridCellParams) => {
          if (params.id === "auto-generated-group-footer-root") {
            return <></>;
          }

          return (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <SquareIcon sx={{ color: params.row.color }} />
              <Box ml={1}>{params.row.name}</Box>
            </Box>
          );
        },
      },
    ];
  };

  const colDefs = getColDefs();

  const updateViewers = async (groupKey?: string | undefined) => {
    await isolateViewersByAttribute(groupKey, props.attributeType, cadModelCategories, appStore);
  };

  const handleRowSelection = async (colIndex?: number | undefined, groupKey?: string | undefined) => {
    console.log(`updateViewers: ${colIndex}, ${groupKey}`);

    await updateViewers(groupKey);

    const event: ResultsEvent = {
      key: "data-grid",
      eventType: ResultsEvents.SELECTION_CHANGED,
      groupKey,
    };

    props.emitter.emit(ResultsEvents.SELECTION_CHANGED, event);
  };

  return (
    <Box sx={{ display: "flex", flexGrow: 1, flexShrink: 0, position: "relative", height: "100%", width: "100%" }}>
      <DataGrid
        rows={rows}
        columns={columns}
        getRowId={(row: any) => row.id}
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "calc(100% - 8px)",
          height: "100%",
        }}
        getRowClassName={(params) => (rowHighlights.has(params.row.groupKey) ? "app-theme--active" : "")}
        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={(selection) => {
          setRowSelectionModel(selection);
          if (selection.length > 0) {
            const row = rows.find((r: MyGridRow) => r.id === selection[0]);
            handleRowSelection(row?.id, row?.name);
          }
        }}
        aggregationModel={aggregationModel}
      />
    </Box>
  );
});

export default ByAttributesDataGrid;
